import { useState, useEffect } from 'react';
import './AdminDashboard.css';

const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return {
    'Authorization': `Bearer ${token}`
  };
};

const GalleryManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [formData, setFormData] = useState({
    caption: '',
    altText: '',
    category: 'general'
  });
  const [editingImage, setEditingImage] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await fetch('http://localhost:3001/api/gallery');
      const data = await response.json();
      setImages(data.images || []);
    } catch (err) {
      showMessage('error', 'Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    console.log('File selected:', file);
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('Preview loaded');
        setPreview(reader.result);
      };
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        showMessage('error', 'Failed to read image file');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      showMessage('error', 'Please select an image');
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('image', selectedFile);
    uploadData.append('title', formData.caption); // Backend expects 'title'
    uploadData.append('description', formData.altText); // Backend expects 'description'
    uploadData.append('category', formData.category);

    try {
      const response = await fetch('http://localhost:3001/api/gallery', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: uploadData
      });

      if (response.ok) {
        showMessage('success', 'Image uploaded successfully');
        resetForm();
        fetchImages();
      } else {
        const error = await response.json();
        showMessage('error', error.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      showMessage('error', 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      const response = await fetch(`http://localhost:3001/api/gallery/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(editingImage)
      });

      if (response.ok) {
        showMessage('success', 'Image updated successfully');
        setEditingImage(null);
        fetchImages();
      } else {
        showMessage('error', 'Update failed');
      }
    } catch (err) {
      showMessage('error', 'Failed to update image');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      const response = await fetch(`http://localhost:3001/api/gallery/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('success', 'Image deleted successfully');
        fetchImages();
      } else {
        showMessage('error', 'Delete failed');
      }
    } catch (err) {
      showMessage('error', 'Failed to delete image');
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPreview(null);
    setFormData({ caption: '', altText: '', category: 'general' });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  if (loading) return <div className="loading">Loading gallery...</div>;

  return (
    <div className="gallery-manager">
      <h2>Gallery Manager</h2>

      {message.text && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      {/* Upload Form */}
      <div className="upload-section">
        <h3>Upload New Image</h3>
        <form onSubmit={handleUpload}>
          <div className="file-upload">
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              id="file-input"
            />
            <label htmlFor="file-input" className="file-label">
              {preview ? 'Change Image' : 'Choose Image'}
            </label>
            {selectedFile && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
              </div>
            )}
            {preview && (
              <div className="preview">
                <img src={preview} alt="Preview" />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Caption</label>
              <input
                type="text"
                value={formData.caption}
                onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                placeholder="Image caption"
              />
            </div>

            <div className="form-group">
              <label>Alt Text</label>
              <input
                type="text"
                value={formData.altText}
                onChange={(e) => setFormData({ ...formData, altText: e.target.value })}
                placeholder="Alt text for accessibility"
              />
            </div>

            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="general">General</option>
                <option value="events">Events</option>
                <option value="community">Community</option>
                <option value="history">History</option>
              </select>
            </div>
          </div>

          <button type="submit" disabled={uploading} className="btn-primary">
            {uploading ? 'Uploading...' : 'Upload Image'}
          </button>
        </form>
      </div>

      {/* Gallery Grid */}
      <div className="images-section">
        <h3>Gallery Images ({images.length})</h3>
        <div className="gallery-grid">
          {images.map((image) => (
            <div key={image.id} className="gallery-item">
              <img 
                src={`http://localhost:3001${image.imageUrl}`} 
                alt={image.title || 'Gallery image'}
                crossOrigin="anonymous"
              />
              <div className="gallery-item-info">
                {editingImage?.id === image.id ? (
                  <div className="edit-form">
                    <input
                      type="text"
                      value={editingImage.title || ''}
                      onChange={(e) => setEditingImage({ ...editingImage, title: e.target.value })}
                      placeholder="Title"
                    />
                    <input
                      type="text"
                      value={editingImage.description || ''}
                      onChange={(e) => setEditingImage({ ...editingImage, description: e.target.value })}
                      placeholder="Description"
                    />
                    <div className="edit-actions">
                      <button onClick={() => handleUpdate(image.id)} className="btn-save">Save</button>
                      <button onClick={() => setEditingImage(null)} className="btn-cancel">Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="caption">{image.title || 'No caption'}</p>
                    <p className="category">{image.category}</p>
                    <div className="gallery-actions">
                      <button onClick={() => setEditingImage(image)} className="btn-edit">Edit</button>
                      <button onClick={() => handleDelete(image.id)} className="btn-delete">Delete</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
        {images.length === 0 && (
          <p className="no-data">No images uploaded yet. Upload your first image above!</p>
        )}
      </div>
    </div>
  );
};

export default GalleryManager;
